/**
 * Spec 10.2.1 §2.3 — account (sub-ledger) view. The ledger page an examiner
 * reads (r 47): statutory particulars, balance regions (available / balance /
 * held / uncleared), a running-balance transaction list per row, and the
 * close-account guard (zero balance + clean; close routes out-of-band).
 */
import { useState } from "react";
import { Link, useParams } from "react-router";
import { useLedgerAccount, useRequestAccountClose } from "../../hooks/use-ledger-workbenches";
import { money } from "../../components/ledger/format";

interface EntryRow {
  direction: string; amount_minor: number; running_balance_minor: number | null;
  txn_no: number | null; type: string; doc_no: string | null; effective_date: string;
  counterparty_name: string | null; reason: string | null; txn_status: string;
}

export function AccountViewPage() {
  const { ledgerId, accountId } = useParams<{ ledgerId: string; accountId: string }>();
  const q = useLedgerAccount(accountId);
  const requestClose = useRequestAccountClose();
  const [closing, setClosing] = useState(false);

  if (q.isLoading) {
    return <div className="screen"><div className="surface"><div className="surface__section"><div className="skeleton sk-line" style={{ width: "50%" }} /><div className="skeleton sk-line" style={{ width: "80%" }} /></div></div></div>;
  }
  if (q.isError || !q.data) {
    return <div className="screen"><div className="surface"><div className="surface__body"><div className="error-state">
      <svg className="mark" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.4}><path d="M12 9v4m0 4h.01M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0Z" /></svg>
      <h3>Couldn’t load this account</h3><p>The ledger page and running balances are unavailable. Retry to reload.</p>
      <button data-variant="primary" onClick={() => q.refetch()}>Retry</button>
    </div></div></div></div>;
  }

  const { account, balance_minor, available_minor, held_minor, uncleared_minor } = q.data;
  const entries = q.data.entries as unknown as EntryRow[];
  const canRequestClose = balance_minor === 0 && account.status === "open";

  return (
    <div className="screen">
      <div className="row" style={{ marginBottom: 10 }}>
        <Link to={`/app/trust/${ledgerId}`} className="muted tiny" style={{ textDecoration: "none" }}>← Cockpit</Link>
      </div>
      <div className="surface">
        <div className="surface__bar">
          <div className="surface__title"><strong>{account.account_no} · {account.title_name}</strong><span className="sub">client sub-ledger{account.status === "closed" ? " · closed" : ""}</span></div>
          <span className="spacer" />
          {/* Spec 10.3.1 §1 — statutory ledger page (r 47) + statement of account (r 52). */}
          <Link to={`/app/print/${ledgerId}/account_ledger?account_id=${accountId}`} target="_blank"><button>Print ledger</button></Link>
          <Link to={`/app/print/${ledgerId}/account_statement?account_id=${accountId}&statement=1`} target="_blank"><button>Statement</button></Link>
          {account.status === "open" ? (
            <button disabled={!canRequestClose || requestClose.isPending} onClick={() => setClosing(true)}>Close account…</button>
          ) : <span className="pill pill--cancelled"><span className="dot" />closed</span>}
        </div>

        {/* statutory particulars (r 47) */}
        <div className="surface__section">
          <div className="section-head"><h3>Statutory particulars</h3></div>
          <dl className="particulars" style={{ gridTemplateColumns: "max-content 1fr max-content 1fr", columnGap: 24 }}>
            <dt>Name</dt><dd>{account.title_name}</dd>
            <dt>Reference</dt><dd>{account.reference ?? "—"}</dd>
            <dt>Address</dt><dd>{account.title_address ?? "—"}</dd>
            <dt>Other party</dt><dd>{account.other_party ?? "—"}</dd>
            <dt>Description</dt><dd>{account.description ?? "—"}</dd>
            <dt>Responsible</dt><dd>{account.responsible_user ?? "—"}</dd>
          </dl>
        </div>

        {/* balance regions */}
        <div className="surface__section">
          <div className="balance-regions">
            <div className="cell cell--primary"><span className="k">Available to draw</span><span className="v money">{money(available_minor)}</span></div>
            <div className="cell"><span className="k">Balance</span><span className="v money">{money(balance_minor)}</span></div>
            <div className="cell cell--held"><span className="k">Held</span><span className="v money">{money(held_minor)}</span></div>
            <div className="cell cell--uncleared"><span className="k">Uncleared</span><span className="v money">{money(uncleared_minor)}</span></div>
          </div>
        </div>

        {/* close-account guard */}
        {closing && account.status === "open" ? (
          <div className="surface__section">
            <div className="section-head"><h3>Close account · {account.title_name}</h3></div>
            <div className="stack">
              {balance_minor === 0
                ? <div className="guard guard--ok"><svg className="mark" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.6}><path d="M20 6 9 17l-5-5" /></svg><div>Balance is {money(0)}. Closing routes out-of-band — a signatory authorises it from the approvals inbox.</div></div>
                : <div className="guard guard--blocked"><svg className="mark" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.6}><path d="M12 9v4m0 4h.01M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0Z" /></svg><div><strong>Balance is {money(balance_minor)}.</strong> A trust account can only close at zero. Pay out or transfer the remaining funds first.</div></div>}
              {requestClose.isError ? <div className="stale-banner"><svg className="icon" viewBox="0 0 24 24"><path d="M12 9v4m0 4h.01M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0Z" /></svg><div>{(requestClose.error as Error).message}</div></div> : null}
              <div className="row"><button onClick={() => setClosing(false)}>Cancel</button><span className="spacer" /><button data-variant="primary" disabled={!canRequestClose || requestClose.isPending} onClick={() => requestClose.mutate({ accountId: accountId! }, { onSuccess: () => setClosing(false) })}>{requestClose.isPending ? "Requesting…" : "Request authorisation to close"}</button></div>
            </div>
          </div>
        ) : null}

        {/* transaction list with running balance */}
        <div className="surface__section">
          <div className="section-head"><h3>Transactions</h3><span className="count">running balance per row</span></div>
          {entries.length === 0 ? (
            <div className="empty-state"><h3>No transactions on this account</h3><p>Statutory particulars are recorded. The ledger page fills as receipts and payments post.</p></div>
          ) : (
            <table className="ledger-table">
              <thead><tr><th>Date</th><th>#</th><th>Particulars</th><th className="num">Debit</th><th className="num">Credit</th><th className="num">Balance</th></tr></thead>
              <tbody>
                {entries.map((e, i) => (
                  <tr key={i}>
                    <td className="row-date">{e.effective_date}</td>
                    <td className="row-doc">{e.doc_no ?? (e.txn_no ? String(e.txn_no) : "—")}</td>
                    <td>{e.reason ?? e.counterparty_name ?? e.type}</td>
                    <td className="num money">{e.direction === "dr" ? money(e.amount_minor) : ""}</td>
                    <td className="num money">{e.direction === "cr" ? money(e.amount_minor) : ""}</td>
                    <td className={`num money${(e.running_balance_minor ?? 0) < 0 ? " money--neg" : ""}`}>{money(e.running_balance_minor)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}
